import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Spinner } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faEdit, faExclamation, faFileExport } from '@fortawesome/free-solid-svg-icons';
import { faTrashAlt } from '@fortawesome/free-regular-svg-icons';
import { get, patch } from 'helpers/requests';
import { PolicyFormContext } from '../policies/PolicyFormContext';
import {
  cellOperation,
  headerOperation,
  headerDoubleRowOperation,
  cellNumOperation,
  cellDateOperation,
} from '../../helpers/tables';
import { fileDownload } from '../../helpers/helpers';
import BaseTable from '../UI/BaseTable';
import { API, BASE_URL, DRAFT_LABEL, EMITTED_LABEL } from '../../CONST';
import isUserHasProfile from '../../helpers/profileValidator';
import { EMIT_WRITE } from '../../helpers/profilePermission';

const EndosoListTable = (props) => {
  const {
    data,
    handleDelete,
    handleEdit,
    handleWarning,
    warningOpen,
    deleteWarningOpen,
    loginIdToken,
  } = props;
  const policyFormState = useContext(PolicyFormContext);
  const [processingId, setProcessingId] = useState(null);

  const isDownloadDisabled = (policyState) => {
    if (!policyState) return true;

    if ((policyState.data && policyState.data.emitido) === true) {
      return false;
    }
    // Helper to check that the field exists and its nested value.id is present.
    const isValidField = (field) => field && field.value && field.value.id;
  
    // List of required fields to validate.
    const fieldsToCheck = [
      // 'condicionado_general',
      'actividad_asegurada',
      'tipo_intermediario',
      'moneda',
      'asegurado',
      'producto',
      'tipo_cobertura',
      'tipologia_ejecucion',
      'tipo_institucion',
      'afianzado',
    ];
  
    const areRequiredFieldsValid = fieldsToCheck.every(
      (field) => isValidField(policyState[field])
    );
  
    let intermediarioValid = false;
    if (
      policyState.tipo_intermediario &&
      policyState.tipo_intermediario.value &&
      policyState.tipo_intermediario.value.id === 1
    ) {
      intermediarioValid = policyState.intermediario === null;
    } else {
      intermediarioValid = isValidField(policyState.intermediario);
    }
    // const giroValid = policyState.giro && policyState.giro.value;

    return !(areRequiredFieldsValid && intermediarioValid); // && giroValid);
  };

  // console.log("policyFormState: ", policyFormState);

  const handleEndosoUpdate = async (i) => {
    const getResponse = await get(`${BASE_URL}${API.endosos}/?poliza_base_id=${i.poliza_base_id}`);
    const endosoData = getResponse[0];
    if (policyFormState.hasChanged === false){
      return
    }

    const garantiaPatchData = {
      base: {
        poliza_maestra_id: policyFormState.condicionado_general ? policyFormState.condicionado_general.value.id : undefined,
        actividad_economica_id: policyFormState.actividad_asegurada.value.id,
        asegurado_alias_id: policyFormState.asegurado_alias === "" ? null : policyFormState.asegurado_alias,
        tipo_intermediario_id: policyFormState.tipo_intermediario.value.id,
        moneda_asegurada_id: policyFormState.moneda.value.id,
        asegurado_id: policyFormState.asegurado.value.id,
        producto_id: policyFormState.producto.value.id,
        tipo_cobertura_id: policyFormState.tipo_cobertura.value.id,
        tipologia_ejecucion_id: policyFormState.tipologia_ejecucion.value.id,
        tipo_institucion_id: policyFormState.tipo_institucion.value.id,
        intermediario_id: policyFormState.intermediario ? policyFormState.intermediario.value.id : null
      },
      afianzado_id: policyFormState.afianzado.value.id,
      actividad_id: policyFormState.giro ? policyFormState.giro.value.value : undefined,
      adjuntos_uuids: [], 
      adjuntos_ids: []
    };

    await patch (`${BASE_URL}${API.garantias}/${i.poliza_base_id}/`, garantiaPatchData);

    if (endosoData.tipo_endoso.id === 1) {
      const patchData = {
        inicio_vigencia: endosoData.inicio_vigencia,
        inicio_endoso: endosoData.inicio_endoso,
        termino_vigencia: endosoData.termino_vigencia,
        cambio_cobertura_amt: endosoData.cambio_cobertura_amt,
        cambio_comision_pct: endosoData.cambio_comision_pct,
        cambio_prima_neta_amt: endosoData.cambio_prima_neta_amt,
        poliza_base_id: endosoData.poliza_base_id,
        tipo_endoso_id: endosoData.tipo_endoso.id,
        glosa: endosoData.glosa,
        beneficiario_id: endosoData.beneficiario_id,
      };

      await patch (`${BASE_URL}${API.endosos}/${i.id}/`, patchData);
    }
  }


  const columns = [
    {
      Header: headerOperation('#'),
      id: 'id',
      accessor: (i) => cellOperation(i.secuencia_endoso),
      width: 50,
      filterable: false,
      sortable: false,
    },
    {
      Header: headerOperation('Emisión'),
      id: 'emision',
      accessor: (i) => cellDateOperation(i.emision),
      width: 95,
      filterable: false,
      sortable: false,
    },
    {
      Header: headerOperation('Tipo'),
      id: 'tipo_endoso',
      accessor: (i) => {
        return cellOperation(
          <div className='alert alert-dark-blue text-center' role="alert">
            {i.tipo_endoso.descripcion}
          </div>,
        );
      },
      width: 160,
      filterable: false,
      sortable: false,
    },
    {
      Header: headerOperation('Inicio Vigencia'),
      id: 'inicio_vigencia',
      accessor: (i) => cellDateOperation(i.inicio_endoso),
      filterable: false,
      sortable: false,
    },
    {
      Header: headerOperation('Fin Vigencia'),
      id: 'fin_vigencia',
      accessor: (i) => cellDateOperation(i.termino_vigencia),
      filterable: false,
      sortable: false,
    },
    {
      Header: headerDoubleRowOperation(<span>&Delta; Monto</span>, 'Garantizado'),
      id: 'monto_garantizado',
      accessor: (i) => cellNumOperation(i.cambio_cobertura_amt),
      filterable: false,
      sortable: false,
    },
    {
      Header: headerOperation(<span>&Delta; Comisión</span>),
      id: 'comision',
      accessor: (i) => cellNumOperation(i.cambio_comision_pct),
      filterable: false,
      sortable: false,
    },
    {
      Header: headerDoubleRowOperation(<span>&Delta; Prima</span>, 'Neta'),
      id: 'prima_neta',
      accessor: (i) => cellNumOperation(i.cambio_prima_neta_amt),
      filterable: false,
      sortable: false,
    },
    {
      Header: headerDoubleRowOperation(<span>&Delta; Prima</span>, 'Exenta'),
      id: 'prima_exenta',
      accessor: (i) => cellNumOperation(i.cambio_prima_exenta_neta),
      filterable: false,
      sortable: false,
    },
    {
      Header: headerOperation('Estado'),
      id: 'estado',
      accessor: (i) => {
        let alertClass = 'alert-success';
        let alertText = EMITTED_LABEL;
        if (!i.emitido) {
          alertClass = 'alert-primary';
          alertText = DRAFT_LABEL;
        }
        return cellOperation(
          <div className={`alert ${alertClass} text-center`} role="alert">
            {alertText}
          </div>,
        );
      },
      width: 110,
      filterable: false,
      sortable: false,
    },
    {
      Header: headerOperation('Acciones'),
      id: 'acciones',
      accessor: (i) => (
        <div
          style={{ display: 'grid', gridTemplateColumns: '1fr 3fr', gridGap: '5px' }}
          className="table-cel"
        >
          <div style={{ textAlign: 'right' }}>
            <Button
              className="table-link policy"
              title="descargar"
              disabled={
                !i.emitido && (
                  !policyFormState ||
                  isDownloadDisabled(policyFormState) ||
                  processingId === i.id
                )
              }
              onClick={async () => {
                if (!policyFormState) return;
                setProcessingId(i.id);
                try {
                  await handleEndosoUpdate(i);
                  await fileDownload(`${BASE_URL}${API.endosos}/${i.id}/download/`, loginIdToken);
                } catch (error) {
                  console.error('Download failed:', error);
                } finally {
                  setProcessingId(null);
                }
              }}
              style={{
                borderColor: 
                  !policyFormState || 
                  isDownloadDisabled(policyFormState) || 
                  processingId === i.id 
                    ? '#cccccc'
                    : 'var(--orsan-blue)',
                background: 'transparent',
                cursor: 
                // eslint-disable-next-line no-nested-ternary
                i.emitido 
                  ? 'pointer' 
                  : (!policyFormState || isDownloadDisabled(policyFormState)) 
                    ? 'not-allowed' 
                    : 'pointer'
              }}
            >
              {processingId === i.id ? (
                <Spinner size="sm" />
              ) : (
                <FontAwesomeIcon icon={faDownload} />
              )}
            </Button>
          </div>
          {i.emitido ? (
            ''
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gridGap: '5px' }}>
              {i.can_edit && (
                <div style={{ textAlign: 'center', margin: '1px 0 0 1px' }} title="editar">
                  <Button
                    className="table-link policy"
                    title="editar"
                    onClick={() => handleEdit(i)}
                  >
                    <FontAwesomeIcon icon={faEdit} />
                  </Button>
                </div>
              )}
              {isUserHasProfile(EMIT_WRITE) && (
                <div style={{ textAlign: 'center' }}>
                  {!warningOpen ? (
                    <Button
                      className="table-link policy"
                      title="emitir"
                      onClick={(e) => handleWarning(e, i.id)}
                    >
                      <FontAwesomeIcon icon={faFileExport} />
                    </Button>
                  ) : (
                    <div className="warning-button">
                      <FontAwesomeIcon icon={faExclamation} />
                    </div>
                  )}
                </div>
              )}
              {i.can_edit && (
                <div style={{ textAlign: 'left' }}>
                  {!deleteWarningOpen ? (
                    <Button
                      className="table-link policy"
                      title="eliminar"
                      onClick={() => handleDelete(i.id)}
                    >
                      <FontAwesomeIcon icon={faTrashAlt} />
                    </Button>
                  ) : (
                    <div className="warning-button">
                      <FontAwesomeIcon icon={faExclamation} />
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      ),
      width: 110,
      filterable: false,
      sortable: false,
    },
  ];
  return (
    <div style={{ margin: '25px 0 40px 0' }}>
      <BaseTable columns={columns} data={data} resizable={false} showPagination noBorders />
    </div>
  );
};

EndosoListTable.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  data: PropTypes.array.isRequired,
  handleDelete: PropTypes.func.isRequired,
  handleEdit: PropTypes.func.isRequired,
  handleWarning: PropTypes.func.isRequired,
  warningOpen: PropTypes.bool.isRequired,
  deleteWarningOpen: PropTypes.bool.isRequired,
  loginIdToken: PropTypes.string.isRequired,
};

export default EndosoListTable;
